import { window } from "@packages/global";
import { Inject, Injectable } from "@webmotors/ioc";

import { LOGGER, type Logger } from "../logger";
import { SESSION_STORAGE_SERVICE } from "./session-storage.types";

@Injectable(SESSION_STORAGE_SERVICE)
export class SessionStorageService {
  /**
   * Logger instance
   *
   */
  private readonly logger: Logger;

  /**
   * SessionStorageService constructor
   *
   * @param loggerFactory
   */
  constructor(@Inject(LOGGER) private readonly loggerFactory: Logger) {
    this.logger = this.loggerFactory.create(SESSION_STORAGE_SERVICE);
  }

  /**
   * Save an item into sessionStorage
   *
   * @param key - The key to save under
   * @param value - The value to save (object or string)
   */
  setItem<T>(key: string, value: T): void {
    if (!window.sessionStorage) {
      this.logger.log("[Set] sessionStorage is not available.");
      return;
    }

    try {
      const serializedValue =
        typeof value === "string" ? value : JSON.stringify(value);
      window.sessionStorage.setItem(key, serializedValue);
      this.logger.log(`[Set] sessionStorage: ${key}`, value);
    } catch (error) {
      this.logger.error(
        `[Set] Failed to set sessionStorage key: ${key}`,
        error
      );
    }
  }

  /**
   * Get an item from sessionStorage
   *
   * @param key - The key to retrieve
   * @returns The value from sessionStorage, or null if not found
   */
  getItem<T>(key: string): T | null {
    if (!window.sessionStorage) {
      this.logger.log("[Get] sessionStorage is not available.");
      return null;
    }

    try {
      const item = window.sessionStorage.getItem(key);
      if (!item) {
        this.logger.log("[Get] sessionStorage key not found");
        return null;
      }
      try {
        const parsedItem = JSON.parse(item);
        this.logger.log(`[Get] sessionStorage "${key}": `, parsedItem);

        return parsedItem as T;
      } catch {
        this.logger.log(`[Get] sessionStorage "${key}": `, item);

        return item as unknown as T;
      }
    } catch (error) {
      this.logger.error(
        `[Get] Failed to get sessionStorage key: ${key}`,
        error
      );
      return null;
    }
  }

  /**
   * Remove an item from sessionStorage
   *
   * @param key - The key to remove
   */
  removeItem(key: string): void {
    if (!window.sessionStorage) {
      this.logger.log("[Remove] sessionStorage is not available.");
      return;
    }

    try {
      window.sessionStorage.removeItem(key);
      this.logger.log(`[Remove] sessionStorage key: ${key}`);
    } catch (error) {
      this.logger.error(
        `[Remove] Failed to remove sessionStorage key: ${key}`,
        error
      );
    }
  }

  /**
   * Clear all items from sessionStorage
   *
   */
  clear(): void {
    if (!window.sessionStorage) {
      this.logger.log("[Clear] sessionStorage is not available.");
      return;
    }

    try {
      window.sessionStorage.clear();
      this.logger.log("[Clear] sessionStorage cleared");
    } catch (error) {
      this.logger.error("[Clear] Failed to clear sessionStorage", error);
    }
  }
}
